/**
 * #23 — tonalite DTMF audible a la frappe.
 *
 * POURQUOI CE FICHIER EXISTE.
 * Doleance : « aucun son quand on compose ». Mesure a l'ecran en instrumentant
 * `AudioContext` : **0 oscillateur emis**, au clic comme a la barre espace.
 * `sendDTMF` transmettait bien le chiffre a Twilio (`call.sendDigits`) mais
 * n'emettait rien localement — et le SDK Twilio ne renvoie pas d'echo audible
 * a l'agent. Il n'y avait donc, litteralement, aucun son a entendre.
 *
 * On le produit ici, localement : une vraie bi-frequence DTMF, celle des
 * telephones. C'est du retour utilisateur, independant de la telephonie — donc
 * verifiable sans ligne reelle, contrairement au reste de la doleance #23.
 *
 * Respecte `sonEstActif()` comme tous les autres emetteurs de l'app.
 */
import { sonEstActif } from '@/state/soundPrefs';

/** Bi-frequences DTMF standard (ITU-T Q.23), en hertz : [ligne, colonne]. */
const FREQUENCES: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

const DUREE_SECONDES = 0.12;

let contexte: AudioContext | null = null;

function obtenirContexte(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const Ctor = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!contexte) contexte = new Ctor();
    // Les navigateurs suspendent le contexte tant qu'aucun geste utilisateur
    // n'a eu lieu ; la frappe EST ce geste, on le reprend a la volee.
    if (contexte.state === 'suspended') void contexte.resume();
    return contexte;
  } catch {
    return null;
  }
}

/**
 * Joue la tonalite du caractere donne. Sans effet si le son est coupe, si le
 * caractere n'est pas une touche de clavier telephonique, ou si le navigateur
 * refuse l'audio — jamais d'exception remontee a l'appelant.
 */
export function jouerTonaliteDTMF(touche: string): void {
  if (!sonEstActif()) return;
  const paire = FREQUENCES[touche];
  if (!paire) return;

  const ctx = obtenirContexte();
  if (!ctx) return;

  try {
    const gain = ctx.createGain();
    // Attaque et extinction douces : un creneau brut claque dans les enceintes.
    const maintenant = ctx.currentTime;
    gain.gain.setValueAtTime(0, maintenant);
    gain.gain.linearRampToValueAtTime(0.12, maintenant + 0.01);
    gain.gain.linearRampToValueAtTime(0, maintenant + DUREE_SECONDES);
    gain.connect(ctx.destination);

    for (const frequence of paire) {
      const oscillateur = ctx.createOscillator();
      oscillateur.type = 'sine';
      oscillateur.frequency.setValueAtTime(frequence, maintenant);
      oscillateur.connect(gain);
      oscillateur.start(maintenant);
      oscillateur.stop(maintenant + DUREE_SECONDES);
    }
  } catch {
    /* audio indisponible : la frappe reste silencieuse, jamais bloquante */
  }
}
